import { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Helmet } from 'react-helmet'
import { getLastOrder, makeDisplayItem } from '@open-tender/utils'
import {
  fetchCustomerFavorites,
  fetchMenuItems,
  selectBrand,
  selectContent,
  selectCustomer,
  selectCustomerFavorites,
  selectCustomerOrders,
  useAppDispatch,
  useAppSelector,
} from '@open-tender/cloud'
import {
  Content,
  ItemCards,
  HeaderDefault,
  Loading,
  Main,
  OrderCardItem,
  PageTitle,
  PageError,
  PageContainer,
  PageContent,
} from 'components'

const Favorites = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { entities, loading, error } = useAppSelector(selectCustomerFavorites)
  const { entities: orders } = useAppSelector(selectCustomerOrders)
  const lastOrder = useMemo(() => getLastOrder(orders), [orders])
  const [favorites, setFavorites] = useState(entities)
  const { title: siteTitle, has_favorites } = useAppSelector(selectBrand) || {}
  const { favorites: config } = useAppSelector(selectContent) || {}
  const { auth } = useAppSelector(selectCustomer)
  const isLoading = loading === 'pending'
  const items = favorites.map((i) => ({ ...i.item }))

  useEffect(() => {
    if (!auth || !has_favorites) return navigate('/account')
  }, [auth, has_favorites, navigate])

  useEffect(() => {
    if (error) window.scrollTo(0, 0)
  }, [error])

  useEffect(() => {
    dispatch(fetchCustomerFavorites())
  }, [dispatch])

  useEffect(() => {
    const favs = entities.map((i) => ({
      ...i,
      item: makeDisplayItem(i.item as any),
    }))
    setFavorites(favs as any)
  }, [entities])

  useEffect(() => {
    if (lastOrder) {
      const { revenue_center, service_type: serviceType } = lastOrder
      const { revenue_center_id: revenueCenterId } = revenue_center
      dispatch(fetchMenuItems({ revenueCenterId, serviceType }))
    }
  }, [lastOrder, dispatch])

  return auth ? (
    <>
      <Helmet>
        <title>
          {config?.title} | {siteTitle}
        </title>
      </Helmet>
      <Content>
        <HeaderDefault />
        <Main>
          <PageContainer style={{ maxWidth: '114rem' }}>
            <PageTitle {...config} />
            <PageError error={error} />
            {items.length ? (
              <ItemCards
                items={items as any}
                delay={0}
                sequential={false}
                renderItem={(props) => <OrderCardItem {...props} />}
              />
            ) : (
              <PageContent>
                {isLoading ? (
                  <Loading text="Retrieving your favorites..." />
                ) : (
                  <p>
                    Looks like you don't have any favorites yet. Please visit
                    the Recent Orders page to add some.
                  </p>
                )}
              </PageContent>
            )}
          </PageContainer>
        </Main>
      </Content>
    </>
  ) : null
}

export default Favorites
